import TypedEmitter from 'typed-emitter';
import Kefir from 'kefir';
import kefirBus from 'kefir-bus';
import cx from 'classnames';

import EventEmitter from '../lib/safe-event-emitter';
import {
  GmailAppMenuItemView,
  NATIVE_CLASS,
  INBOXSDK_CLASS,
  ICON_ELEMENT_SELECTOR,
} from '../dom-driver/gmail/views/gmail-app-menu-item-view';
import {
  AppMenuItemDescriptor,
  AppMenuItemPanelDescriptor,
} from '../namespaces/app-menu';
import {
  NATIVE_CLASS as PANEL_NATIVE_CLASS,
  CollapsiblePanelView,
} from './collapsible-panel-view';
import GmailDriver from '../dom-driver/gmail/gmail-driver';
import { addCollapsiblePanel } from '../dom-driver/gmail/gmail-driver/add-collapsible-panel';
import GmailElementGetter from '../dom-driver/gmail/gmail-element-getter';
import defer from '../../common/defer';

type MessageEvents = {
  /**
   * Fires when this AppMenuItemView's menuItem has a `mouseleave` event triggered.
   */
  blur: () => void;
  /**
   * Fires when this AppMenuItemView's menuItem has a `click` event triggered.
   */
  click: () => void;
  /**
   * Fires when this AppMenuItemView's menuItem has a `mouseenter` event triggered.
   */
  hover: () => void;
  destroy: () => void;
};

enum NativeAppMenuClassName {
  MAIL = 'XJ',
  CHAT = 'ao4',
  SPACES = 'aIt',
  MEET = 'adZ',
}

// Native Gmail menu button => the panel class names mapping
const nativeButtonToPanelMap = new Map([
  [NativeAppMenuClassName.MAIL, 'aIH'], // Mail
  [NativeAppMenuClassName.CHAT, 'arL'], // Chat
  [NativeAppMenuClassName.SPACES, 'arM'], // Spaces
  [NativeAppMenuClassName.MEET, undefined], // Meet
]);

/**
 * Mail routes list is not exhaustive
 */
const MAIL_ROUTES = ['inbox/:page'];
const CHAT_ROUTES = ['chat/welcome', 'chat/dm/:chatID'];
const SPACES_ROUTES = ['rooms/welcome', 'chat/space/:spaceID'];
const MEET_ROUTES = ['calls'];

const routeIDtoMenuItemClass = Object.fromEntries([
  ...MAIL_ROUTES.map((route) => [route, NativeAppMenuClassName.MAIL] as const),
  ...CHAT_ROUTES.map((route) => [route, NativeAppMenuClassName.CHAT] as const),
  ...SPACES_ROUTES.map(
    (route) => [route, NativeAppMenuClassName.SPACES] as const,
  ),
  ...MEET_ROUTES.map((route) => [route, NativeAppMenuClassName.MEET] as const),
]);

type StreamType =
  | ['route-activated', HTMLElement]
  | ['click', HTMLElement]
  | ['mouseenter' | 'mouseleave', HTMLElement, MouseEvent];

/**
 * Each AppMenuItemView represents an entry in the app menu of Gmail. Typically the main action of a AppMenuItemView is performed when the user clicks or hovers on the app menu item.
 *
 * @note Contains both a native Gmail app menu item and a collapsible panel.
 */
export class AppMenuItemView extends (EventEmitter as new () => TypedEmitter<MessageEvents>) {
  static #menuItemChangeStream = Kefir.pool<StreamType, unknown>();
  static #menuItemChangeBus = kefirBus<StreamType, unknown>();
  static #menuItemAddedDeferred = defer<undefined>();
  static #routeViewDriverStream: ReturnType<
    GmailDriver['getRouteViewDriverStream']
  >;
  static #menuItemToPanelMap = new Map<HTMLElement, HTMLElement | undefined>();
  static #appMenuItemViews = new Set<AppMenuItemView>();

  static getAllPanels() {
    const appMenuElement = GmailElementGetter.getAppMenuContainer();

    const nativePanels = appMenuElement?.querySelectorAll<HTMLElement>(
      `.${PANEL_NATIVE_CLASS}`,
    );

    return Array.from(nativePanels ?? []);
  }

  static getActivePanel(useSdkActiveSelector = true) {
    const appMenuElement = GmailElementGetter.getAppMenuContainer();
    const { ACTIVE, SDK_ACTIVE } = CollapsiblePanelView.elementCss;

    const nativePanel = appMenuElement?.querySelector<HTMLElement>(
      useSdkActiveSelector
        ? `.${PANEL_NATIVE_CLASS}.${ACTIVE}.${SDK_ACTIVE}`
        : `.${PANEL_NATIVE_CLASS}.${ACTIVE}`,
    );

    return nativePanel;
  }

  static deactivatePanel(panel: HTMLElement) {
    const { ACTIVE, HOVER, SDK_ACTIVE } = CollapsiblePanelView.elementCss;

    panel.style.removeProperty('height');
    if (panel.classList.contains(HOVER)) {
      panel.classList.remove(HOVER);
    }
    if (panel.classList.contains(ACTIVE)) {
      panel.classList.remove(ACTIVE);
    }
    if (panel.classList.contains(SDK_ACTIVE)) {
      panel.classList.remove(SDK_ACTIVE);
    }
  }

  static isMenuItem(element: HTMLElement) {
    return element.classList.contains(NATIVE_CLASS);
  }

  static getAllMenuItems() {
    const appMenuElement = GmailElementGetter.getAppMenuContainer();
    return Array.from(
      appMenuElement?.querySelectorAll<HTMLElement>(`.${NATIVE_CLASS}`) ?? [],
    );
  }

  static getActiveMenuItem() {
    const appMenuElement = GmailElementGetter.getAppMenuContainer();
    const { ACTIVE, SDK_ACTIVE } = GmailAppMenuItemView.elementCss;

    const button = appMenuElement?.querySelector<HTMLElement>(
      `.${NATIVE_CLASS}.${ACTIVE}.${SDK_ACTIVE}`,
    );

    return button;
  }

  static deactivateMenuItem(menuItem: HTMLElement) {
    const { ACTIVE, HOVER, SDK_ACTIVE } = GmailAppMenuItemView.elementCss;

    if (menuItem.classList.contains(HOVER)) {
      menuItem.classList.remove(HOVER);
    }
    if (menuItem.classList.contains(ACTIVE)) {
      menuItem.classList.remove(ACTIVE);
    }
    if (menuItem.classList.contains(SDK_ACTIVE)) {
      menuItem.classList.remove(SDK_ACTIVE);
    }
  }

  static #isPanelLess() {
    const activePanel = AppMenuItemView.getActivePanel(false);
    return !activePanel;
  }
  static #adjustTooltipNub() {
    // adjust all menu items' collapsible panel's tooltip nub position
    const appMenuElement = GmailElementGetter.getAppMenu();
    for (const menuItem of appMenuElement?.querySelectorAll<HTMLElement>(
      `.${NATIVE_CLASS}`,
    ) ?? []) {
      const panel = AppMenuItemView.#menuItemToPanelMap.get(menuItem);
      if (!panel) continue;

      const nubElement = panel?.querySelector<HTMLElement>('* > .aTV');
      const iconElement = menuItem?.querySelector<HTMLElement>('.V6');

      const iconElementBox = iconElement?.getBoundingClientRect();
      const offsetTop =
        (iconElement?.offsetTop ?? 0) + (iconElementBox?.height ?? 0) * 0.5;
      const nubSide = 17;
      const nubDiagonal = nubSide * Math.sqrt(2);
      const offsetNub = nubDiagonal / 4;
      const offset = offsetTop - offsetNub;

      if (nubElement && offset) {
        nubElement.style.top = `${offset}px`;
      }
    }
  }
  static #isPanelDropdownShown() {
    const sdkDropdown = document.querySelector<HTMLElement>('.inboxsdk__menu');
    if (sdkDropdown) {
      return true;
    }
    const mailDropdown = document.querySelector<HTMLElement>('.JM');
    if (mailDropdown && mailDropdown.style.display !== 'none') {
      return true;
    }
    const chatAndSpacesDropdown = document.querySelector<HTMLElement>('.tP');
    if (
      chatAndSpacesDropdown &&
      chatAndSpacesDropdown.style.display !== 'none'
    ) {
      return true;
    }
    return false;
  }
  static #setUpRouteViewDriverStream(driver: GmailDriver) {
    if (AppMenuItemView.#routeViewDriverStream) return;

    AppMenuItemView.#routeViewDriverStream = driver.getRouteViewDriverStream();

    // Activate menu items on route changes. Used for deep-links, browser history navigation etc.
    AppMenuItemView.#routeViewDriverStream.onValue(async (routeView) => {
      const routeViewID = routeView.getRouteID();
      const routeType = routeView.getRouteType();

      /* Handling custom app menu items */
      const descriptors = Array.from(
        AppMenuItemView.#appMenuItemViews,
        async (appMenuItemView) => {
          const gmailView = await appMenuItemView.#gmailViewPromise;
          return [gmailView.menuItemDescriptor, gmailView] as const;
        },
      );

      const results = await Promise.all(descriptors);

      const [, gmailView] =
        results.find(([descriptor]) => {
          return descriptor?.isRouteActive(routeView as any);
        }) ?? [];

      if (gmailView?.element) {
        AppMenuItemView.#menuItemChangeBus.emit([
          'route-activated',
          gmailView.element,
        ]);
        return;
      }

      /* Handling native app menu items */
      let menuItemClass = '';
      if (routeViewID) {
        menuItemClass = routeIDtoMenuItemClass[routeViewID];
      }

      if ((!routeViewID || !menuItemClass) && routeType !== 'CUSTOM') {
        menuItemClass = NativeAppMenuClassName.MAIL;
      }

      const menuItemElement =
        menuItemClass &&
        document.querySelector<HTMLElement>(`.${menuItemClass}`);

      if (menuItemElement) {
        AppMenuItemView.#menuItemChangeBus.emit([
          'route-activated',
          menuItemElement,
        ]);
      }
    });
  }
  static {
    (async function init() {
      const gmailAppMenu = await GmailElementGetter.getAppMenuAsync();
      if (!gmailAppMenu) {
        return;
      }

      AppMenuItemView.#menuItemChangeStream.plug(
        AppMenuItemView.#menuItemChangeBus,
      );

      // Set up menu item panel -> menu collapsible panel element mappings
      for (const [menuItemClass, panelClass] of nativeButtonToPanelMap) {
        const menuItemElement = document.querySelector<HTMLElement>(
          `.${NATIVE_CLASS}.${menuItemClass}`,
        );
        const panelElement = panelClass
          ? document.querySelector<HTMLElement>(
              `.${PANEL_NATIVE_CLASS}.${panelClass}`,
            )
          : null;

        if (menuItemElement) {
          AppMenuItemView.#menuItemToPanelMap.set(
            menuItemElement,
            panelElement ?? undefined,
          );
        }
      }
    })();
  }
  static {
    AppMenuItemView.#menuItemAddedDeferred.promise.then(() => {
      // Set up event listeners we use to control menu UI
      // We intercept some events emitted on Gmail (native) menu items to gain full control of the menu (menu items and panels).
      // Thus, some native event handlers won't be invoked so we replicate the Gmail's behavior by ourselves.
      const appMenuElement = GmailElementGetter.getAppMenuContainer();
      for (const nativeMenuItem of appMenuElement?.querySelectorAll<HTMLElement>(
        `.${NATIVE_CLASS}:not(.${INBOXSDK_CLASS})`,
      ) ?? []) {
        // Gmail uses this listener for panel hover state activation. It's prevented from propagation and we handle UI updates
        const buttonElement =
          nativeMenuItem.querySelector<HTMLElement>('.V6.CL');
        buttonElement?.addEventListener(
          'mouseenter',
          (e) => {
            e.stopImmediatePropagation();
            AppMenuItemView.#menuItemChangeBus.emit([
              'mouseenter',
              nativeMenuItem,
              e,
            ]);
          },
          true,
        );

        // Gmail uses this listener for panel hover state deactivation. It's prevented from propagation and we handle UI updates
        nativeMenuItem.addEventListener(
          'mouseleave',
          (e) => {
            e.stopImmediatePropagation();
            AppMenuItemView.#menuItemChangeBus.emit([
              'mouseleave',
              nativeMenuItem,
              e,
            ]);
          },
          true,
        );

        // Click event isn't prevented from propagation to let Gmail handle routing. The styling is handled by us
        nativeMenuItem.addEventListener('click', () => {
          AppMenuItemView.#menuItemChangeBus.emit(['click', nativeMenuItem]);
        });

        // Handle moving cursor out of panel. We use it to enhance the styling since it won't be handled by Gmail
        const panel = AppMenuItemView.#menuItemToPanelMap.get(nativeMenuItem);
        panel?.addEventListener('mouseleave', (e) => {
          e.stopImmediatePropagation();
          AppMenuItemView.#menuItemChangeBus.emit([
            'mouseleave',
            nativeMenuItem,
            e,
          ]);
        });
      }
    });
  }
  static {
    AppMenuItemView.#menuItemChangeStream.onValue(async (v) => {
      const [type, menuItem, mouseEvent] = v;
      const { HOVER } = GmailAppMenuItemView.elementCss;

      function handleActivate() {
        const appMenuElement = GmailElementGetter.getAppMenuContainer();

        // deactivate menu items and handle keyboard accessibility
        for (const menuItem_ of appMenuElement?.querySelectorAll<HTMLElement>(
          `.${NATIVE_CLASS}`,
        ) ?? []) {
          AppMenuItemView.deactivateMenuItem(menuItem_);
          // update tabindex
          const iconElement = menuItem_.querySelector<HTMLElement>(
            ICON_ELEMENT_SELECTOR,
          );
          if (iconElement) {
            iconElement.tabIndex = menuItem_ === menuItem ? 0 : -1;
          }
        }

        // activate menu item
        activateMenuItem(menuItem);
        // activate panel
        const panel = AppMenuItemView.#menuItemToPanelMap.get(menuItem);
        if (panel) {
          const otherPanels = AppMenuItemView.getAllPanels().filter(
            (p) => p !== panel,
          );

          for (const otherPanel of otherPanels) {
            AppMenuItemView.deactivatePanel(otherPanel);
          }

          activatePanel(panel);
        }
      }

      switch (type) {
        case 'mouseenter': {
          const panel = AppMenuItemView.#menuItemToPanelMap.get(menuItem);
          const activeMenuItem = AppMenuItemView.getActiveMenuItem();
          const activePanel = AppMenuItemView.getActivePanel();
          const burgerMenuOpen = GmailElementGetter.isAppBurgerMenuOpen();

          // hover-styled panel is displayed for collapsed burger menu
          if (
            activeMenuItem === menuItem &&
            activePanel === panel &&
            burgerMenuOpen
          )
            return;

          // deactivate active panel
          if (activePanel) {
            AppMenuItemView.deactivatePanel(activePanel);
          }

          // activate new panel
          if (panel) {
            const height =
              window.innerHeight - panel.getBoundingClientRect().top;
            panel.style.cssText = `height: ${height}px;`;
            activatePanel(panel, { hover: true });
            // hover menu item
            if (!menuItem.classList.contains(HOVER)) {
              menuItem.classList.add(HOVER);
            }
            // handle burger menu collapsed
            if (!burgerMenuOpen) {
              for (const panel of document.querySelectorAll(
                CollapsiblePanelView.elementSelectors.NATIVE,
              ) ?? []) {
                panel.classList.add(
                  CollapsiblePanelView.elementCss.COLLAPSED_HOVER,
                );
              }
            }
          } else {
            // panel doesn't exist (i.e. Meet) so activate active menu item's panel instead (as Gmail currently does -- FEB 2023)
            const panel =
              activeMenuItem &&
              AppMenuItemView.#menuItemToPanelMap.get(activeMenuItem);
            if (panel) {
              activatePanel(panel);
            }
          }

          break;
        }

        case 'mouseleave': {
          // Do not handle blur event when we're in one of the panel's dropdowns
          const isPanelDropdownShown = AppMenuItemView.#isPanelDropdownShown();
          if (isPanelDropdownShown) return;

          const activeMenuItem = AppMenuItemView.getActiveMenuItem();
          const activePanel = AppMenuItemView.getActivePanel();
          const activeMenuItemPanel =
            activeMenuItem &&
            AppMenuItemView.#menuItemToPanelMap.get(activeMenuItem);

          if (
            mouseEvent.relatedTarget instanceof Node &&
            (menuItem.contains(mouseEvent.relatedTarget) ||
              activePanel?.contains(mouseEvent.relatedTarget))
          )
            return;

          const burgerMenuOpen = GmailElementGetter.isAppBurgerMenuOpen();

          if (activePanel === activeMenuItemPanel && burgerMenuOpen) return;

          // unhover menu item
          if (menuItem.classList.contains(HOVER)) {
            menuItem.classList.remove(HOVER);
          }

          // handle burger menu collapsed
          for (const panel of document.querySelectorAll(
            CollapsiblePanelView.elementSelectors.NATIVE,
          ) ?? []) {
            panel.classList.remove(
              CollapsiblePanelView.elementCss.COLLAPSED_HOVER,
            );
          }

          // deactivate active panel
          if (activePanel) {
            AppMenuItemView.deactivatePanel(activePanel);
          }

          // activate active menu item panel
          if (activeMenuItemPanel) {
            activatePanel(activeMenuItemPanel);
          }
          break;
        }

        case 'click': {
          handleActivate();

          // handle panel-less mode
          const isPanelLess = AppMenuItemView.#isPanelLess();

          for (const panel of document.querySelectorAll(
            CollapsiblePanelView.elementSelectors.NATIVE,
          ) ?? []) {
            panel.classList.toggle(
              CollapsiblePanelView.elementCss.PANEL_LESS,
              isPanelLess,
            );
          }
          GmailElementGetter.getAppMenu()?.classList.toggle('aTO', isPanelLess);
          GmailElementGetter.getAppHeader()?.classList.toggle(
            'aTO',
            isPanelLess,
          );
          break;
        }

        case 'route-activated': {
          handleActivate();
          break;
        }
      }
    });
  }

  get menuItemDescriptor() {
    return this.#menuItemDescriptor;
  }

  #driver;
  #destroyed = false;
  #gmailViewPromise: Promise<GmailAppMenuItemView>;
  #collapsiblePanel?: CollapsiblePanelView;
  #menuItemDescriptor;

  constructor(driver: GmailDriver, menuItemDescriptor: AppMenuItemDescriptor) {
    super();
    this.#driver = driver;
    this.#menuItemDescriptor = menuItemDescriptor;

    const gmailAppMenuItemView =
      this.#driver.addAppMenuItem(menuItemDescriptor);

    this.#gmailViewPromise = gmailAppMenuItemView;

    gmailAppMenuItemView.then((gmailView) => {
      if (this.#destroyed || !gmailView) {
        return; //we have been removed already
      }
      const gmailElement = gmailView.element;

      if (gmailElement) {
        AppMenuItemView.#menuItemToPanelMap.set(gmailElement, undefined);
        AppMenuItemView.#adjustTooltipNub();
      }

      gmailView.on('destroy', () => {
        this.remove();
      });

      gmailView.on('click', () => {
        AppMenuItemView.#menuItemChangeBus.emit(['click', gmailElement!]);
        this.emit('click');
      });

      gmailView.on('hover', (e) => {
        AppMenuItemView.#menuItemChangeBus.emit([
          'mouseenter',
          gmailElement!,
          e,
        ]);
        this.emit('hover');
      });

      gmailView.on('blur', (e) => {
        AppMenuItemView.#menuItemChangeBus.emit([
          'mouseleave',
          gmailElement!,
          e,
        ]);
        this.emit('blur');
      });
    });

    AppMenuItemView.#setUpRouteViewDriverStream(driver);
    AppMenuItemView.#menuItemAddedDeferred.resolve(undefined);
    AppMenuItemView.#appMenuItemViews.add(this);
  }

  /**
   * Add a panel to an AppMenuItemView.
   *
   * @param panelDescriptor A single descriptor for the app menu item panel.
   *
   * @returns A promise that resolves to the CollapsiblePanelView instance. The result is undefined if the SDK doesn't detect the AppMenu being shown.
   */
  async addCollapsiblePanel(panelDescriptor: AppMenuItemPanelDescriptor) {
    const descriptor = {
      ...panelDescriptor,
      className: cx(panelDescriptor.className, {
        [CollapsiblePanelView.elementCss.COLLAPSED]:
          !GmailElementGetter.isAppBurgerMenuOpen(),
        [CollapsiblePanelView.elementCss.PANEL_LESS]:
          AppMenuItemView.#isPanelLess(),
      }),
    };

    const collapsiblePanel = await addCollapsiblePanel(
      this.#driver,
      descriptor,
      this.menuItemDescriptor.insertIndex,
    );

    if (collapsiblePanel) {
      this.#gmailViewPromise.then((gmailView) => {
        const gmailElement = gmailView.element;

        if (gmailElement) {
          collapsiblePanel.on('blur', (e) => {
            AppMenuItemView.#menuItemChangeBus.value([
              'mouseleave',
              gmailElement,
              e,
            ]);
          });

          AppMenuItemView.#menuItemToPanelMap.set(
            gmailElement,
            collapsiblePanel.element,
          );
          AppMenuItemView.#adjustTooltipNub();
        }
      });
    }

    this.#collapsiblePanel = collapsiblePanel;
    return collapsiblePanel;
  }

  /**
   * Updates the AppMenuItem's AppMenuItemDescriptor
   *
   * @param menuItemDescriptor The new descriptor for the app menu item panel.
   */
  async update(menuItemDescriptor: AppMenuItemDescriptor) {
    const gmailView = await this.#gmailViewPromise;
    gmailView.menuItemDescriptor = menuItemDescriptor;
    this.#menuItemDescriptor = menuItemDescriptor;
  }

  /**
   * Remove this AppMenuItemView from its parent
   */
  remove() {
    if (this.#destroyed) return;
    this.#destroyed = true;
    this.emit('destroy');
    this.#collapsiblePanel?.remove();
    this.#gmailViewPromise.then((gmailView) => {
      gmailView.element &&
        AppMenuItemView.#menuItemToPanelMap.delete(gmailView.element);
      gmailView.remove();
      AppMenuItemView.#adjustTooltipNub();
    });
    AppMenuItemView.#appMenuItemViews.delete(this);
  }
}

function activatePanel(panel: HTMLElement, options?: { hover?: boolean }) {
  const { ACTIVE, HOVER, SDK_ACTIVE } = CollapsiblePanelView.elementCss;

  if (!panel.classList.contains(ACTIVE)) {
    panel.classList.add(ACTIVE);
  }
  if (!panel.classList.contains(SDK_ACTIVE)) {
    panel.classList.add(SDK_ACTIVE);
  }
  if (options?.hover) {
    if (!panel.classList.contains(HOVER)) {
      panel.classList.add(HOVER);
    }
  } else {
    if (panel.classList.contains(HOVER)) {
      panel.classList.remove(HOVER);
    }
  }
}

function activateMenuItem(menuItem: HTMLElement) {
  const { ACTIVE, SDK_ACTIVE } = GmailAppMenuItemView.elementCss;

  if (!menuItem.classList.contains(ACTIVE)) {
    menuItem.classList.add(ACTIVE);
  }
  if (!menuItem.classList.contains(SDK_ACTIVE)) {
    menuItem.classList.add(SDK_ACTIVE);
  }
}
